/**
 * dview:TrackGraphViewer: the track as a node with the tracks it connects to on either side.
 * Neighbours come from its begin and end (dview:begin, dview:end, dview:neighbours) and from
 * the net topology (dview:topology, a table with km columns): a neighbour lies before or after
 * the track by its km. Edges show whether a train can pass (dview:relations, from
 * rtm:hasNavigability). What the track is part of sits above, what sits on it below.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { pickLang, setting, settingList } from '@utils/viewShapes.utils';
import { rowCount } from '../page';
import { num } from '../linearLocation';
import { uriText } from './values';
import type { Widget, WidgetContext, WidgetProps } from './types';
import styles from './trackGraph.module.scss';

const W = 1000;
const ROW = 46;

interface Side {
  before: string[];
  after: string[];
}

function rowIris(context: WidgetContext, key: string | undefined): string[] {
  const row = key ? context.model?.rows[key] : undefined;
  if (!row) return [];
  const values = (context.model?.values[row.id] ?? [])
    .filter((value) => value.type === 'uri')
    .map((value) => value.value);
  const items = (context.model?.tables[row.id]?.items ?? []).map((item) => item.iri);
  return [...values, ...items];
}

/** Neighbours on each side, without the track itself. */
export function neighbours(property: WidgetProps['property'], context: WidgetContext): Side {
  const self = context.model?.uri ?? '';
  const [beginKey, endKey] = settingList(property, 'neighbours');
  const before = new Set(rowIris(context, beginKey));
  const after = new Set(rowIris(context, endKey));
  const topology = context.model?.rows[setting(property, 'topology') ?? ''];
  const kmOf = (key: string) => num(context.model?.values[context.model.rows[key]?.id ?? ''] ?? []);
  const from = kmOf('km-from');
  const to = kmOf('km-to');
  const middle = from !== null && to !== null ? (from + to) / 2 : null;
  for (const item of (topology && context.model?.tables[topology.id]?.items) || []) {
    if (before.has(item.iri) || after.has(item.iri)) continue;
    const a = num(item.cells['km-from']);
    const b = num(item.cells['km-to']);
    const theirs = a !== null && b !== null ? (a + b) / 2 : null;
    if (middle !== null && theirs !== null && theirs > middle) after.add(item.iri);
    else before.add(item.iri);
  }
  before.delete(self);
  after.delete(self);
  return { before: Array.from(before).slice(0, 6), after: Array.from(after).slice(0, 6) };
}

export type Passage = 'both' | 'none' | 'unknown';

/** How a train can pass between the track and a neighbour, from the net relations between them
 * (rtm:hasNavigability: Both, AB, BA or None). One-way relations count as passable. */
export function passages(
  property: WidgetProps['property'],
  context: WidgetContext
): Map<string, Passage> {
  const self = context.model?.uri ?? '';
  const row = context.model?.rows[setting(property, 'relations') ?? ''];
  const map = new Map<string, Passage>();
  for (const relation of (row && context.model?.tables[row.id]?.items) || []) {
    const value = relation.cells.navigability?.[0]?.value ?? '';
    const passage: Passage = value === 'None' ? 'none' : value ? 'both' : 'unknown';
    const a = new Set((relation.cells.a ?? []).map((cell) => cell.value));
    const b = new Set((relation.cells.b ?? []).map((cell) => cell.value));
    const others = a.has(self) ? b : b.has(self) ? a : null;
    if (!others) continue;
    for (const other of others) {
      if (other === self) continue;
      // Passable over any relation wins over a relation that cannot be passed.
      if (map.get(other) !== 'both') map.set(other, passage);
    }
  }
  return map;
}

const TrackGraph = ({ property, context }: WidgetProps): JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const model = context.model;
  if (!model) return null;
  const self = model.uri;
  const { before, after } = neighbours(property, context);
  const passage = passages(property, context);
  const ends = {
    begin: rowIris(context, setting(property, 'begin'))[0],
    end: rowIris(context, setting(property, 'end'))[0],
  };
  const directionRow = model.rows[setting(property, 'direction') ?? ''];
  const direction = directionRow ? model.values[directionRow.id]?.[0]?.value : undefined;
  const line = model.allProperties.find(
    (row) => row.path.kind === 'predicate' && row.path.iri.endsWith('/onRailwayLine')
  );
  const lineIri = line
    ? model.values[line.id]?.find((value) => value.type === 'uri')?.value
    : undefined;
  const counts = settingList(property, 'counts')
    .map((key) => {
      const row = model.rows[key];
      const count = rowCount(model, key);
      return count > 0 && row
        ? t(`entity.template.badge.${key}`, {
            count,
            value: count,
            defaultValue: `${count} ${pickLang(row.name, context.language) ?? key}`,
          })
        : null;
    })
    .filter(Boolean)
    .join(' · ');

  const rows = Math.max(before.length, after.length, 1);
  const H = Math.max(220, rows * ROW + 110);
  const midY = H / 2;
  const cx = W / 2;
  const open = (iri: string) => navigate(uriToDereferencePath(iri));
  const className =
    model.nodeShapes.map((shape) => pickLang(shape.label, context.language)).find(Boolean) ?? '';
  // Track numbers alone ("2") read as "Track 2".
  const named = (text: string) =>
    (text.length <= 6 && className ? `${className} ${text}` : text).slice(0, 20);
  const selfLabel = named(uriText(self, context));
  const neighbourLabel = (iri: string) => named(uriText(iri, context));
  const pillW = (text: string) => Math.min(170, Math.max(70, text.length * 8 + 28));

  const node = (iri: string, x: number, y: number, kind: 'self' | 'other') => {
    const text = kind === 'self' ? selfLabel : neighbourLabel(iri);
    const w = pillW(text);
    return (
      <g
        key={iri}
        className={kind === 'self' ? styles.self : styles.node}
        onClick={kind === 'self' ? undefined : () => open(iri)}
        role={kind === 'self' ? undefined : 'link'}
        aria-label={text}
      >
        <title>{uriText(iri, context)}</title>
        <rect x={x - w / 2} y={y - 17} width={w} height={34} rx={17} />
        <text x={x} y={y + 5} textAnchor="middle">
          {text}
        </text>
      </g>
    );
  };

  const fan = (items: string[], side: -1 | 1) =>
    items.map((iri, index) => {
      const y = midY + (index - (items.length - 1) / 2) * ROW;
      const x = cx + side * 330;
      const startX = cx + side * (pillW(selfLabel) / 2 + 4);
      const endX = x - side * (pillW(neighbourLabel(iri)) / 2 + 4);
      const kind = passage.get(iri) ?? 'unknown';
      const arrows = kind === 'both' ? 'url(#dim-graph-arrow)' : undefined;
      return (
        <g key={iri}>
          <path
            d={`M${startX} ${midY}C${(startX + endX) / 2} ${midY} ${(startX + endX) / 2} ${y} ${endX} ${y}`}
            className={styles.edge}
            data-passage={kind}
            markerStart={arrows}
            markerEnd={arrows}
          >
            <title>{t(`entity.template.graph.passage.${kind}`)}</title>
          </path>
          {kind === 'none' && (
            <text
              x={(startX + endX) / 2}
              y={(midY + y) / 2 - 8}
              textAnchor="middle"
              className={styles.cannotPass}
            >
              {t('entity.template.graph.passage.none')}
            </text>
          )}
          {node(iri, x, y, 'other')}
        </g>
      );
    });

  const endLabel = (iri: string | undefined, side: -1 | 1) =>
    iri && (
      <text
        x={cx + side * 150}
        y={midY - 10}
        textAnchor="middle"
        className={styles.endText}
        onClick={() => open(iri)}
        role="link"
        tabIndex={0}
        onKeyDown={(event) => event.key === 'Enter' && open(iri)}
      >
        {uriText(iri, context)}
      </text>
    );

  return (
    <figure className={styles.graph}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.svg}
        role="group"
        aria-label={t('entity.template.graph.label')}
      >
        <defs>
          <marker
            id="dim-graph-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="9"
            markerHeight="9"
            markerUnits="userSpaceOnUse"
            orient="auto-start-reverse"
          >
            <path d="M0 0L10 5L0 10z" className={styles.arrowHead} />
          </marker>
        </defs>
        <path d={`M${cx} ${midY - 17}V36 M${cx} ${midY + 17}V${H - 30}`} className={styles.stem} />
        {lineIri && (
          <text
            x={cx}
            y={26}
            textAnchor="middle"
            className={styles.context}
            onClick={() => open(lineIri)}
            role="link"
            tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && open(lineIri)}
          >
            {uriText(lineIri, context)} · {t('entity.template.graph.partOf')}
          </text>
        )}
        {fan(before, -1)}
        {fan(after, 1)}
        {endLabel(ends.begin, -1)}
        {endLabel(ends.end, 1)}
        {node(self, cx, midY, 'self')}
        {counts && (
          <text x={cx} y={H - 12} textAnchor="middle" className={styles.context}>
            {counts}
          </text>
        )}
        {before.length === 0 && after.length === 0 && (
          <text x={cx} y={midY + 44} textAnchor="middle" className={styles.empty}>
            {t('entity.template.graph.noNeighbours')}
          </text>
        )}
      </svg>
      <figcaption className={styles.caption}>
        {direction
          ? t(`entity.template.graph.direction.${direction}`, direction)
          : t('entity.template.graph.noDirection')}
        {' · '}
        {t('entity.template.graph.source')}
      </figcaption>
    </figure>
  );
};

export const TrackGraphViewer: Widget = (props) => <TrackGraph {...props} />;
